import React from 'react';
import classNames from 'classnames';
import { useDropzone, Accept } from 'react-dropzone';

import LoadingSpinner, {
  SIZES as SPINNER_SIZES
} from '@buyside/components/presentation/LoadingSpinner/loadingSpinner';
import DropzoneFile from '@buyside/components/presentation/Dropzone/DropzoneFile';

import styles from '@buyside/components/presentation/Dropzone/dropzoneInputStyles.scss';

export interface DropzoneInputProps {
  accept: Accept;
  analyticsTag?: string;
  disabled?: boolean;
  id?: string;
  isLoading?: boolean;
  onDrop: (files: File[]) => void;
  onRemove?: () => void;
  filename: string | null;
}

const DropzoneInput: React.FC<DropzoneInputProps> = ({
  accept,
  analyticsTag,
  disabled = false,
  id,
  isLoading = false,
  onDrop,
  onRemove,
  filename
}): React.ReactElement => {
  const acceptableExtensions = Object.values(accept).reduce(
    (memo, arr) => Array.from(new Set([...memo, ...arr.map(el => el)])),
    []
  );

  const {
    getInputProps,
    getRootProps,
    isDragAccept,
    isDragActive,
    isDragReject,
    open
  } = useDropzone({
    accept: accept,
    disabled: disabled || isLoading,
    multiple: false,
    noClick: Boolean(filename),
    onDrop
  });

  const dropzoneAreaStyles = classNames(styles.dropzoneArea, {
    [styles.dropzoneAreaDragAccept]: isDragAccept,
    [styles.dropzoneAreaDragReject]: isDragReject,
    [styles.dropzoneAreaContainsFile]: filename,
    [styles.dropzoneAreaDisabled]: disabled || isLoading
  });

  return (
    <>
      <div
        data-testid="dropzone-input-container"
        className={dropzoneAreaStyles}
        {...getRootProps()}
      >
        <input
          data-testid="dropzone-input-area"
          id={id}
          data-heap={analyticsTag}
          {...getInputProps()}
        />
        {filename && (
          <DropzoneFile loading={isLoading} filename={filename} open={open} onRemove={onRemove} />
        )}
        {!filename && isLoading && <LoadingSpinner size={SPINNER_SIZES.TINY} />}
        {!isDragReject && !filename && !isLoading && (
          <>
            {isDragActive ? <p>Drop file here</p> : <p>Drag file here, or click to select file</p>}
          </>
        )}
        {isDragReject && <p>Invalid file type</p>}
      </div>
      <div className={styles.acceptedFileTypes}>
        Accepted file types: {acceptableExtensions.join(', ')}
      </div>
    </>
  );
};

export default DropzoneInput;
